import React, { useState, useEffect, useRef, useCallback } from 'react'
import StepperComponent from './stepper_component'
import './stepper.scss'
import Axios from '../../utilities/axios'

/**
 * @type {Array<{title: string, link: string, fontAwesomeIconID: string}>}
 */
// const fileDetails = [{
//   title: 'amazon_list.json',
//   link: 'https://example.com',
//   path: 'My Drive/amazon_list.json',
//   fontAwesomeIconID: 'fal fa-file-alt'
// }, {
//   title: 'amazon_list.json',
//   link: 'https://example.com',
//   path: 'My Drive/hello/path/to/file/amazon/amazon_list.json',
//   fontAwesomeIconID: 'fal fa-file-alt'
// },{
//   title: 'amazon_list.json',
//   link: 'https://example.com',
//   path: 'My Drive/amazon_list.json',
//   fontAwesomeIconID: 'fal fa-file-alt'
// }, {
//   title: 'amazon_list.json',
//   link: 'https://example.com',
//   path: 'My Drive/amazon_list.json',
//   fontAwesomeIconID: 'fal fa-file-alt'
// },{
//   title: 'amazon_list.json',
//   link: 'https://example.com',
//   path: 'My Drive/amazon_list.json',
//   fontAwesomeIconID: 'fal fa-file-alt'
// }, {
//   title: 'amazon_list.json',
//   link: 'https://example.com',
//   path: 'My Drive/amazon_list.json',
//   fontAwesomeIconID: 'fal fa-file-alt'
// },{
//   title: 'amazon_list.json',
//   link: 'https://example.com',
//   path: 'My Drive/amazon_list.json',
//   fontAwesomeIconID: 'fal fa-file-alt'
// }, {
//   title: 'amazon_list.json',
//   link: 'https://example.com',
//   path: 'My Drive/amazon_list.json',
//   fontAwesomeIconID: 'fal fa-file-alt'
// }]
const fileDetails = [{
  title: 'amazon_list.json',
  link: 'https://example.com',
  path: 'My Drive/amazon_list.json',
  icon: 'fal fa-file-alt'
}, {
  title: 'amazon_list.json',
  link: 'https://example.com',
  path: 'My Drive/hello/path/to/file/amazon/amazon_list.json',
  icon: 'fal fa-file-alt'
}]

const TaskProgress = ({ processID = null, finishedAt = null, pausedAt = null, fileDetails = {} }) => {
  const [steps, setSteps] = useState([])
  const [displayState, setDisplayState] = useState('connecting') // 'connecting', 'connected', 'no_process', 'already_connected', 'error'
  const [statusMessage, setStatusMessage] = useState(null)
  const [isCompleted, setIsCompleted] = useState(false)
  const [isNotificationExpanded, setIsNotificationExpanded] = useState(false)
  const [extraStatusMessage, setExtraStatusMessage] = useState('')
  const [isPaused, setIsPaused] = useState(false)
  const [isFinished, setIsFinished] = useState(false)

  const eventSourceRef = useRef(null)
  const reconnectAttemptsRef = useRef(0)
  const reconnectTimeoutRef = useRef(null)
  const isPausedRef = useRef(isPaused)

  const maxReconnectAttempts = 5
  const maxBackoffTime = 30000 // 30 seconds


  const baseURL = process.env.NODE_ENV === 'development'
    ? 'http://localhost:4000'
    : 'https://sellagen.com'

  // Calculate exponential backoff delay
  const getBackoffDelay = useCallback((attempt) => {
    const delay = Math.min(1000 * Math.pow(2, attempt), maxBackoffTime)
    return delay
  }, [])

  // Map backend step data to frontend format
  const mapStepData = useCallback((backendStep) => {
    const formattedTime = backendStep.timestamp
      ? new Date(backendStep.timestamp).toLocaleTimeString()
      : new Date().toLocaleTimeString()

    const type = ['finish', 'pause'].includes(backendStep?.id) ? 'done' : 'running'

    return {
      stepNumber: backendStep.step || backendStep.stepNumber,
      title: backendStep.title || 'Processing',
      type,
      details: backendStep.brief || backendStep.details || '',
      timestamp: formattedTime,
      toolID: backendStep.id,
      extra: backendStep.extra
    }
  }, [])

  // Update last step type (e.g., from 'running' to 'completed')
  const updateLastStepType = useCallback((type) => {
    setSteps(prev => {
      if (prev.length === 0) return prev
      const updated = [...prev]
      updated[updated.length - 1] = {
        ...updated[updated.length - 1],
        type
      }
      // Keep only the latest 100 steps
      if (updated.length > 100) {
        return updated.slice(-100)
      }
      return updated
    })
  }, [])

  // Connect to SSE endpoint
  const connect = useCallback(async (forced=false) => {
    // Clean up existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close()
      eventSourceRef.current = null
    }

    // Clear any pending reconnect timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }

    setDisplayState('connecting')
    setStatusMessage(null)

    try {
      // Establish SSE connection
      const eventSource = new EventSource(`${baseURL}/api/scheduler/feed/${processID}${forced === true ? '/connect' : ''}`, {withCredentials: true})
      eventSourceRef.current = eventSource

      // Handle connection open
      eventSource.onopen = () => {
        setDisplayState('connected')
        setStatusMessage(null)
        reconnectAttemptsRef.current = 0
      }

      // Handle 'initial' event - load existing steps
      eventSource.addEventListener('initial', (event) => {
        try {
          const data = JSON.parse(event.data)
          if (data.steps && Array.isArray(data.steps)) {
            // make control pausable
            if (isPausedRef.current) {
              setIsPaused(false)
              setExtraStatusMessage('')
            }
            const mappedSteps = data.steps.map(mapStepData)
            setSteps(mappedSteps)
            // Mark all but last as completed
            if (mappedSteps.length > 0) {
              setSteps(prev => prev.map((step, idx) =>
                idx < prev.length - 1 ? { ...step, type: 'completed' } : step
              ))
            }
          }
        } catch (err) {
          console.error('Error parsing initial event:', err)
        }
      })

      // Handle 'step' event - new step added
      eventSource.addEventListener('step', (event) => {
        try {
          const data = JSON.parse(event.data)
          const newStep = mapStepData(data)

          // make control pausable
          if (isPausedRef.current) {
            setIsPaused(false)
            setExtraStatusMessage('')
          }

          // Mark previous step as completed
          let stepType = 'completed'
          if (data?.prevStatus === 'error') {
            stepType = 'error'
          }
          updateLastStepType(stepType)

          // Add new step and keep only latest 100
          setSteps(prev => {
            const updated = [...prev, newStep]
            return updated.length > 100 ? updated.slice(-100) : updated
          })
        } catch (err) {
          console.error('Error parsing step event:', err)
        }
      })

      // Handle 'complete' event
      eventSource.addEventListener('complete', (event) => {
        try {
          // Mark last step as completed
          updateLastStepType('completed')
          setIsCompleted(true)
          setDisplayState('completed')

          const data = JSON.parse(event.data)
          if (data?.id === 'pause') {
            setIsPaused(true)
            setExtraStatusMessage('')
          } else if (data?.id === 'finish') {
            setIsFinished(true)
            setExtraStatusMessage('')
          }
          const newStep = mapStepData(data)

          // Add new step and keep only latest 100
          setSteps(prev => {
            const updated = [...prev, newStep]
            return updated.length > 100 ? updated.slice(-100) : updated
          })

          // Close connection
          if (eventSourceRef.current) {
            eventSourceRef.current.close()
            eventSourceRef.current = null
          }
        } catch (err) {
          console.error('Error parsing complete event:', err)
        }
      })

      // Handle 'error' event from backend
      eventSource.addEventListener('error', (event) => {
        try {
          // updateLastStepType('error')
          setDisplayState('error')

          // Close connection
          if (eventSourceRef.current) {
            eventSourceRef.current.close()
            eventSourceRef.current = null
          }
        } catch (err) {
          console.error('Error parsing error event:', err)
        }
      })

      // Handle 'disconnect' event - replaced by new subscriber
      eventSource.addEventListener('disconnect', (event) => {
        try {
          const data = JSON.parse(event.data)
          setExtraStatusMessage('')

          setStatusMessage(data.reason || 'Connection was replaced by another subscriber')
          setDisplayState('already_connected')

          // Close connection
          if (eventSourceRef.current) {
            eventSourceRef.current.close()
            eventSourceRef.current = null
          }
        } catch (err) {
          console.error('Error parsing disconnect event:', err)
        }
      })

      // Handle connection errors
      eventSource.onerror = async () => {
        // Close connection
        if (eventSourceRef.current) {
          eventSourceRef.current.close()
          eventSourceRef.current = null
        }

        // Try to get error details
        try {
          await Axios.get(
            `${baseURL}/api/scheduler/feed/${processID}${forced === true ? '/connect' : ''}`,
            { withCredentials: true }
          )
        } catch (err) {
          const status = err?.response?.status
          const data = err?.response?.data

          if (status === 404) {
            setDisplayState('no_process')
            setStatusMessage(data?.message)
            return
          }

          if (status === 409) {
            setDisplayState('already_connected')
            setStatusMessage(data?.message)
            return
          }
        }

        // Generic error - attempt reconnect
        setDisplayState('error')
        setStatusMessage('Connection lost')

        // Only attempt reconnect if we haven't completed
        if (!isCompleted) {
          attemptReconnect()
        }
      }

    } catch (err) {
      setDisplayState('error')
      setStatusMessage('Failed to establish connection')
      attemptReconnect()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processID, mapStepData, updateLastStepType])

  // Attempt to reconnect with exponential backoff
  const attemptReconnect = useCallback(() => {
    if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
      setStatusMessage(`Failed to reconnect after ${maxReconnectAttempts} attempts`)
      setDisplayState('error')
      return
    }

    const delay = getBackoffDelay(reconnectAttemptsRef.current)
    reconnectAttemptsRef.current += 1

    setStatusMessage(`Reconnecting in ${Math.round(delay / 1000)}s... (attempt ${reconnectAttemptsRef.current}/${maxReconnectAttempts})`)
    setDisplayState('error')

    reconnectTimeoutRef.current = setTimeout(() => {
      connect()
    }, delay)
  }, [connect, getBackoffDelay])

  // Manual reconnect function
  const manualReconnect = useCallback((forced=false) => {
    reconnectAttemptsRef.current = 0
    connect(forced)
  }, [connect])

  const controlTask = async (operation) => {
    if (!['pause', 'resume'].includes(operation)) return
    if (extraStatusMessage) return
    setExtraStatusMessage('Working on it')
    try {
      const response = await Axios.post('/scheduler/control', { processID, operation })
      if (response?.data?.success === true) {
        if (operation === 'pause') {
          setExtraStatusMessage('Preparing to pause...')
        } else {
          setExtraStatusMessage('Preparing to resume...')
          /// connect
          manualReconnect()
        }
      }
    } catch (error) {
      // do nothing
      setExtraStatusMessage('')
    }
  }

  // Connect on mount
  useEffect(() => {
    if (finishedAt) {
      const finished = {
        id: 'finish',
        stepNumber: null,
        title: 'Scale Mode Task Complete',
        brief: 'Task is complete',
        extra: '',
        prevStatus: null,
        timestamp: finishedAt
      }
      setSteps([mapStepData(finished)])
      setDisplayState('completed')
      setIsFinished(true)
      setIsCompleted(true)
    } else if (pausedAt) {
      const paused = {
        id: 'pause',
        stepNumber: null,
        title: 'Scale Mode Task Paused',
        brief: 'Task is paused',
        extra: '',
        prevStatus: null,
        timestamp: pausedAt
      }
      setSteps([mapStepData(paused)])
      setDisplayState('completed')
      setIsPaused(true)
      setIsCompleted(true)
    } else {
      connect()
    }

    // Cleanup on unmount
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close()
        eventSourceRef.current = null
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
        reconnectTimeoutRef.current = null
      }
    }
  }, [connect])

  // Keep ref in sync with state
  useEffect(() => {
    isPausedRef.current = isPaused
  }, [isPaused])

  const renderDropdown = () => {
    if (fileDetails?.preview?.length === 0 || !isPaused) return null

    return (
      <div className='progress-dropdown'>
        <div className='progress-dropdown-button'>
          {/* <i className="far fa-chevron-down"></i> */}
          <i className="fal fa-file-alt"></i>
        </div>
        <div className='progress-dropdown-content'>
          <div className='progress-dropdown-inner'>
            <div className='progress-dropdown-title'>Processed Files</div>
            {fileDetails?.preview?.map((file, index) => (
              <div
                key={index}
                className='progress-dropdown-item'
                onClick={() => window.open(file.link, '_blank')}
              >
                {file?.icon ? <i className={file.icon}></i> : <i className='fal fa-file-alt'></i>}
                <div className='progress-dropdown-item-info'>
                  <span className='progress-dropdown-item-title'>{file.fileName}</span>
                  <span className='progress-dropdown-item-path'>{file.filePath}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  const renderControl = () => {
    if (['connected', 'completed'].includes(displayState)) {
      const progressStatus = extraStatusMessage && <div className='progress-status'>{extraStatusMessage}</div>
      if (isPaused) {
        return (
          <>
            <div className='progress-control' onClick={() => controlTask('resume') }>
              <i className="fas fa-play-circle"></i>
              Resume
            </div>
            {/* {renderDropdown()} */}
            {progressStatus ? progressStatus : renderDropdown()}
          </>
        )
      } else if (isFinished) {
        return null
      } else {
        return (
          <>
            <div className='progress-control' onClick={() => controlTask('pause') }>
              <i className="far fa-pause-circle"></i>
              Pause
            </div>
            {progressStatus}
          </>
        )
      }
    }
    return null
  }

  // Render different states
  const renderContent = () => {
    if (displayState === 'connecting') {
      return (
        <div className='progress-message'>
          <div className='progress-message-icon'><i className="fal fa-sync"></i></div>
          <div className='progress-message-text'>Connecting to process...</div>
        </div>
      )
    }

    if (displayState === 'no_process') {
      return (
        <div className='progress-message'>
          <div className='progress-message-icon'><i className="far fa-tasks"></i></div>
          <p>
            {statusMessage || 'No process found or already completed'}
          </p>
        </div>
      )
    }

    if (displayState === 'already_connected') {
      return (
        <div className='progress-message'>
          <div className='progress-force-connect' onClick={() => {manualReconnect(true)} }>Connect</div>
          <div className='progress-message-icon'><i className="far fa-exclamation-circle"></i></div>
          <p>
            {statusMessage || 'Job progress is being monitored in another window'}
          </p>
        </div>
      )
    }

    // Connected state - show stepper
    return <StepperComponent steps={steps} autoScroll={true} />
  }

  if (!processID) {
    return null
  }

  return (
    <div className='progress-container' data-theme='dark'>
      <div className='progress-title'>
        <i className="far fa-window" style={{marginRight: '5px'}} ></i>
        Scale Mode Task Status
        {renderControl()}
      </div>
      {renderContent()}

      {/* Error/Warning Notification Panel */}
      {displayState === 'error' && (
        <ErrorDisplay 
          isNotificationExpanded={isNotificationExpanded}
          setIsNotificationExpanded={setIsNotificationExpanded}
          manualReconnect={manualReconnect} 
          statusMessage={statusMessage} 
          reconnectAttemptsRef={reconnectAttemptsRef}
        />
      )}
    </div>
  )
}

const ErrorDisplay = ({ isNotificationExpanded, setIsNotificationExpanded, maxReconnectAttempts=5, manualReconnect=()=>{}, statusMessage, reconnectAttemptsRef }) => {
  return (
    <div className='stepper-notification-panel'>
      <div
        className={`stepper-notification-compact ${isNotificationExpanded ? 'notification-expanded' : ''}`}
      >
        <div className='stepper-notification-header' onClick={() => setIsNotificationExpanded(!isNotificationExpanded)}>
          <div style={{
            fontSize: '14px',
            color: '#f87171',
            flexShrink: 0,
          }}>
            <i className="far fa-exclamation-circle"></i>
          </div>
          <div className='stepper-title'>Connection Error</div>
          <div className='stepper-expand-button'>
            {isNotificationExpanded ? <i className="fas fa-caret-down"></i> : <i className="fas fa-caret-up"></i>}
          </div>
        </div>

        {isNotificationExpanded && (
          <div key={'notification-body-smle'} className='notification-body'>
            <div className='stepper-message'>
              {statusMessage || 'Connection error occurred'}
            </div>
            {reconnectAttemptsRef.current >= maxReconnectAttempts && (
              <button className='stepper-retry-button' onClick={manualReconnect}>
                Retry Connection
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
  
export default TaskProgress